/**
 * Map outbox action types to API calls (replay on reconnect).
 */

import * as diagnosticService from '../services/diagnostic.service.js';
import * as parcoursService from '../services/parcours.service.js';
import * as fondsService from '../services/fonds.service.js';
import { uploadInscriptionProof } from '../services/beneficiaires.service.js';
import { getProofBlob, deleteProofBlob } from './outbox.service.js';
import { mergeDerivedPrereqStatus } from '../utils/parcoursPrereqs.js';
import {
  INSCRIPTION_ETAPE_TITRE,
  isInscriptionEtapeTitle,
  isPreviewEtapeId,
} from '../constants/inscriptionEtapes.js';

const DOCUMENT_CAS_ORDER = ['CAS-A', 'CAS-B', 'CAS-C', 'CAS-D', 'CAS-E', 'CAS-F', 'CAS-G', 'CAS-H'];

function titreIsInscriptionEtape(titre) {
  if (!titre || typeof titre !== 'string') return false;
  const t = titre.toLowerCase();
  return (
    t.includes('inscription') ||
    t.includes('inscri') ||
    t.includes('liste electorale') ||
    t.includes('liste électorale') ||
    t.includes('carte retirée') ||
    t.includes('inscrit sur la liste')
  );
}

function getDocumentEtapesForSync(etapes, docCasCodes) {
  if (!Array.isArray(etapes)) return [];
  let list = etapes.filter((e) => !titreIsInscriptionEtape(e?.titre));
  const codes = docCasCodes?.length ? docCasCodes : DOCUMENT_CAS_ORDER;
  if (codes.length === 1) {
    list = list.filter((e) => (e.cas_numero || e.cas) === codes[0]);
  } else if (codes.length > 1) {
    list = list.filter((e) => codes.includes(e.cas_numero || e.cas));
  }
  return list;
}

async function handleFirstSave(payload) {
  const {
    beneficiaireId,
    diagnosticPayload,
    docCodesForSave,
    previewDocumentEtapes,
    batchUpdatesFromPreview,
  } = payload;

  const diagResponse = await diagnosticService.effectuerDiagnostic(
    beneficiaireId,
    diagnosticPayload,
  );
  if (!diagResponse?.success) {
    throw new Error(diagResponse?.error || 'Erreur lors du diagnostic');
  }

  let parcoursResponse = await parcoursService.genererParcours(beneficiaireId);
  let docCount = getDocumentEtapesForSync(
    parcoursResponse?.data?.etapes || [],
    docCodesForSave,
  ).length;
  const minDocSteps = previewDocumentEtapes?.length || 1;
  if (parcoursResponse?.success && docCount < minDocSteps) {
    await new Promise((r) => setTimeout(r, 500));
    parcoursResponse = await parcoursService.genererParcours(beneficiaireId);
    docCount = getDocumentEtapesForSync(
      parcoursResponse?.data?.etapes || [],
      docCodesForSave,
    ).length;
  }
  if (!parcoursResponse?.success || !parcoursResponse?.data?.etapes) {
    throw new Error(parcoursResponse?.error || 'Erreur lors de la génération du parcours');
  }
  if (docCount < minDocSteps) {
    throw new Error(
      'Le parcours généré est incomplet. Réessayez une fois en ligne.',
    );
  }

  const docEtapesApi = getDocumentEtapesForSync(parcoursResponse.data.etapes, docCodesForSave);
  const previewSteps = Array.isArray(previewDocumentEtapes) ? previewDocumentEtapes : [];
  const updates =
    batchUpdatesFromPreview ||
    previewSteps
      .map((pe, i) => {
        const etape = docEtapesApi[i];
        if (!etape?.id) return null;
        return {
          etapeId: etape.id,
          document_statut: pe.document_statut || 'needed',
          // Prérequis dérivés d'une étape voisine (perte → timbre, attestation →
          // certificat) : reportés ici comme en ligne, sinon le rejeu hors-ligne
          // se ferait refuser « Prérequis non satisfaits ».
          dependency_status: mergeDerivedPrereqStatus(previewSteps, pe),
        };
      })
      .filter(Boolean);

  if (updates.length > 0) {
    const batchRes = await parcoursService.updateEtapesBatchWithRetry(beneficiaireId, updates, {
      maxRetries: 2,
    });
    if (!batchRes?.success) {
      throw new Error(batchRes?.error || 'Échec de la mise à jour du parcours');
    }
    const failed = batchRes.data?.failed || [];
    if (failed.length > 0) {
      throw new Error(`Échec de mise à jour pour ${failed.length} étape(s)`);
    }
  }
  return { success: true };
}

async function handleUpdateEtapes(payload) {
  const { beneficiaireId, batchUpdates } = payload;
  if (!batchUpdates?.length) return { success: true };
  const batchRes = await parcoursService.updateEtapesBatchWithRetry(beneficiaireId, batchUpdates, {
    maxRetries: 2,
  });
  if (!batchRes?.success) {
    throw new Error(batchRes?.error || 'Échec de la mise à jour du parcours');
  }
  const failed = batchRes.data?.failed || [];
  if (failed.length > 0) {
    throw new Error(`Échec de mise à jour pour ${failed.length} étape(s)`);
  }
  return { success: true };
}

async function handleInscriptionPhase(payload) {
  const { beneficiaireId, sameCommune } = payload;
  const res = await parcoursService.createInscriptionPhase(beneficiaireId, sameCommune);
  if (!res?.success) {
    throw new Error(res?.error || 'Impossible de créer la phase d\'inscription');
  }
  return res;
}

/**
 * Une étape d'inscription créée HORS LIGNE n'a qu'un id local (`preview-…`) : le
 * serveur ne la connaît pas encore. À la synchronisation, l'action
 * `interaction.inscriptionPhase` la crée pour de bon — avec un id généré côté
 * serveur, que l'action `inscriptionSave` enfilée juste après ne pouvait pas deviner.
 *
 * Sans cette résolution, le rejeu visait `/parcours/etapes/preview-inscription-0`,
 * recevait 404, échouait 5 fois puis passait en `error` : le travail du relais était
 * PERDU EN SILENCE — précisément dans le cas d'usage (zone rurale, réseau
 * intermittent) pour lequel le mode hors ligne existe.
 *
 * L'ordre FIFO de l'outbox (tri par id auto-incrémenté) garantit que la phase est
 * créée avant ce rejeu ; il ne reste qu'à relire le parcours et retrouver l'étape
 * par son TITRE, seul identifiant stable entre les deux mondes.
 */
async function resolvePreviewEtapeIds(beneficiaireId, updates) {
  const hasPreview = (updates || []).some((u) => isPreviewEtapeId(u.etapeId));
  if (!hasPreview) return updates;

  const res = await parcoursService.obtenirParcours(beneficiaireId);
  const etapes = res?.data?.etapes || [];
  const inscriptionEtapes = etapes.filter((e) => isInscriptionEtapeTitle(e.titre));

  if (inscriptionEtapes.length === 0) {
    // La phase n'existe toujours pas côté serveur : on échoue explicitement pour que
    // l'outbox retente plus tard, plutôt que d'envoyer un id factice.
    throw new Error(
      "L'étape d'inscription n'existe pas encore sur le serveur — nouvelle tentative à la prochaine synchronisation.",
    );
  }

  return (updates || []).map((u) => {
    if (!isPreviewEtapeId(u.etapeId)) return u;
    // Format courant : une seule étape d'inscription. Ancien format : on retombe sur
    // la première, le libellé exact ayant été perdu avec l'id local.
    const real = inscriptionEtapes.find((e) => e.titre === INSCRIPTION_ETAPE_TITRE)
      || inscriptionEtapes[0];
    return { ...u, etapeId: real.id };
  });
}

async function handleInscriptionSave(payload) {
  const { beneficiaireId } = payload;
  const updates = await resolvePreviewEtapeIds(beneficiaireId, payload.updates);
  const failed = [];
  for (const u of updates || []) {
    try {
      const res = await parcoursService.mettreAJourEtapeWithRetry(u.etapeId, u.body, {
        maxRetries: 2,
      });
      if (!res?.success) {
        failed.push({ etapeId: u.etapeId, error: res?.error || 'Échec de mise à jour' });
      }
    } catch (err) {
      failed.push({
        etapeId: u.etapeId,
        error: err?.response?.data?.error || err?.message || 'Échec de mise à jour',
      });
    }
  }
  if (failed.length > 0) {
    const msg = failed.map((f) => `${f.etapeId}: ${f.error}`).join('; ');
    throw new Error(`Échec de mise à jour pour ${failed.length} étape(s): ${msg}`);
  }
  return { success: true };
}

async function handleQuestionnaireComplete(payload) {
  const { beneficiaireId, diagnosticPayload, createInscriptionPhase: shouldCreate, sameCommune } =
    payload;
  const diagResponse = await diagnosticService.effectuerDiagnostic(
    beneficiaireId,
    diagnosticPayload,
  );
  if (!diagResponse?.success) {
    throw new Error(diagResponse?.error || 'Erreur lors du diagnostic');
  }
  if (shouldCreate) {
    return handleInscriptionPhase({ beneficiaireId, sameCommune });
  }
  const parcoursRes = await parcoursService.genererParcours(beneficiaireId);
  if (!parcoursRes?.success) {
    throw new Error(parcoursRes?.error || 'Erreur lors de la génération du parcours');
  }
  return { success: true };
}

async function handleProofUpload(action) {
  const { beneficiaireId, etapeId } = action.payload;
  const blobRow = await getProofBlob(action.id);
  if (!blobRow?.blob) {
    throw new Error('Photo de preuve introuvable sur l\'appareil');
  }
  // Une preuve mise en file hors ligne peut viser une étape créée LOCALEMENT
  // (id `preview-*`) : même règle que inscriptionSave — résoudre l'id au rejeu,
  // jamais l'envoyer tel quel. Sans cela : 404, retentatives épuisées, et la photo
  // du relais disparaît sans un mot. Si la phase n'existe pas encore côté serveur,
  // resolvePreviewEtapeIds lève — l'outbox retentera plus tard.
  const [{ etapeId: realEtapeId }] = await resolvePreviewEtapeIds(beneficiaireId, [{ etapeId }]);
  const file = new File(
    [blobRow.blob],
    blobRow.fileName || `preuve-${Date.now()}.jpg`,
    { type: blobRow.mimeType || 'image/jpeg' },
  );
  const res = await uploadInscriptionProof(beneficiaireId, realEtapeId, file);
  if (!res?.success) {
    throw new Error(res?.error || 'Erreur lors de l\'envoi de la preuve');
  }
  await deleteProofBlob(action.id);
  return res;
}

/** Poignée de main fonds — replay idempotent (le serveur renvoie l'existant). */
async function handleFondsMarkSent(payload) {
  const res = await fondsService.marquerEnvoye(payload.demandeId, { channel: payload.channel });
  if (!res?.success) throw new Error(res?.error || "Erreur lors de la déclaration d'envoi");
  return res;
}

async function handleFondsConfirmReceived(payload) {
  const res = await fondsService.confirmerReception(payload.demandeId);
  if (!res?.success) throw new Error(res?.error || 'Erreur lors de la confirmation de réception');
  return res;
}

const HANDLERS = {
  'interaction.firstSave': handleFirstSave,
  'interaction.updateEtapes': handleUpdateEtapes,
  'interaction.inscriptionPhase': handleInscriptionPhase,
  'interaction.inscriptionSave': handleInscriptionSave,
  'interaction.questionnaireComplete': handleQuestionnaireComplete,
  'proof.upload': handleProofUpload,
  'fonds.markSent': handleFondsMarkSent,
  'fonds.confirmReceived': handleFondsConfirmReceived,
};

export async function executeOutboxAction(action) {
  const handler = HANDLERS[action.type];
  if (!handler) {
    throw new Error(`Type d'action inconnu : ${action.type}`);
  }
  if (action.type === 'proof.upload') {
    return handler(action);
  }
  return handler(action.payload);
}
